export type Theme = "light" | "dark" | "system";

/** Keep in step with THEME_SCRIPT — both sides read the same key. */
export const THEME_STORAGE_KEY = "empite-theme";

const listeners = new Set<() => void>();

function resolve(theme: Theme) {
  return (
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches)
  );
}

/** Writes the class the pre-paint theme script sets on first paint. */
export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", resolve(theme));
}

export function getTheme(): Theme {
  try {
    return (localStorage.getItem(THEME_STORAGE_KEY) as Theme) ?? "system";
  } catch {
    return "system";
  }
}

export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode — the choice just will not persist */
  }
  applyTheme(theme);
  listeners.forEach((listener) => listener());
}

/** Subscribes to both our own writes and OS-level scheme changes. */
export function subscribeTheme(listener: () => void) {
  listeners.add(listener);
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = () => {
    if (getTheme() === "system") applyTheme("system");
    listener();
  };
  media.addEventListener("change", onSystemChange);
  return () => {
    listeners.delete(listener);
    media.removeEventListener("change", onSystemChange);
  };
}
